import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import PhotosOfWeek from "@/components/PhotosOfWeek";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Upload } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/i18n/i18n";

/** Full photo archive: everything that no longer fits in the dashboard. */
export default function Gallery() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const fileRef = useRef(null);
  const isStaff = ["superadmin", "academic_director", "teacher"].includes(user?.role);
  const isTeacher = user?.role === "teacher";

  const upload = async (e) => {
    const files = [...(e.target.files || [])];
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post("/wall/photos", fd, { params: caption ? { caption } : {} });
      }
      setCaption("");
      setReload((n) => n + 1);
      toast.success(t(isTeacher ? "gallery_sent_for_review" : "gallery_published"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("gallery_upload_failed"));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      <PageHeader
        title={t("gallery")}
        subtitle={t("gallery_sub")}
        testId="gallery-header"
      />

      {isStaff && (
        <Card className="p-4 mb-6 flex flex-wrap items-center gap-2" data-testid="gallery-upload-card">
          <Input className="max-w-xs" placeholder={t("gallery_caption_ph")} value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="gallery-caption-input" />
          <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="gallery-upload-button">
            <Upload className="h-4 w-4" /> {t("gallery_upload")}
          </Button>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" multiple className="hidden" onChange={upload} data-testid="gallery-upload-input" />
          {isTeacher && <span className="text-xs text-slate-500">{t("gallery_pending_note")}</span>}
        </Card>
      )}

      <PhotosOfWeek key={reload} limit={500} title={t("gallery_all_photos")} showGalleryLink={false} groupByWeek manage />
    </div>
  );
}
